import type { ReactNode } from "react";
import type { Player } from "../../../shared/models/Player";
import { posColor } from "../../../shared/utils/posColor";

export type SleeperLeague = {
  league_id?: string;
  name?: string;
  season?: string;
  draft_id?: string;
  draft_order?: Record<string, number>;
  slot_to_roster_id?: Record<string, number | string>;
  total_rosters?: number;
  roster_positions?: string[];
  settings?: {
    reserve_allow_na?: number;
    reserve_allow_doubtful?: number;
    reserve_allow_out?: number;
    reserve_allow_sus?: number;
    reserve_allow_covid?: number;
    reserve_allow_pup?: number;
    reserve_allow_dnr?: number;
    reserve_allow_sspd?: number;
    reserve_slots?: number;
    taxi_slots?: number;
    playoff_week_start?: number;
  };
  scoring_settings?: Record<string, number>;
};

export type SleeperUser = {
  user_id?: string;
  display_name?: string;
  username?: string;
  avatar?: string;
  metadata?: {
    team_name?: string;
  };
};

export type SleeperRoster = {
  roster_id?: number;
  owner_id?: string;
  players?: string[];
};

export type SleeperTradedPick = {
  season?: string | number;
  round?: string | number;
  owner_id?: string | number;
  previous_owner_id?: string | number;
  roster_id?: string | number;
};

export type SleeperDraft = {
  draft_id?: string;
  draft_order?: Record<string, number>;
  slot_to_roster_id?: Record<string, number | string>;
  settings?: {
    rounds?: number | string;
  };
};

export type SleeperTransactionDraftPick = {
  season?: string | number;
  round?: string | number;
  roster_id?: string | number;
  previous_owner_id?: string | number;
  owner_id?: string | number;
};

export type SleeperTransactionWaiverBudget = {
  sender?: string | number;
  receiver?: string | number;
  amount?: string | number;
};

export type SleeperTransaction = {
  type?: string;
  transaction_id?: string;
  status_updated?: number;
  status?: string;
  roster_ids?: Array<string | number>;
  metadata?: Record<string, unknown> | null;
  leg?: number;
  drops?: Record<string, string | number> | null;
  draft_picks?: SleeperTransactionDraftPick[];
  creator?: string;
  created?: number;
  consenter_ids?: Array<string | number>;
  adds?: Record<string, string | number> | null;
  waiver_budget?: SleeperTransactionWaiverBudget[];
};

export type DynastyRosterPlayer = {
  id: string;
  name: string;
  position: string;
  team: string;
  value: number | null;
  imageUrl?: string;
  pickSeason?: number;
  pickRound?: number;
  pickSlot?: number;
};

export type DynastyRosterTeam = {
  rosterId: number;
  displayName: string;
  avatarUrl?: string;
  playerCount: number;
  totalValue: number;
  players: DynastyRosterPlayer[];
  starters: { slot: string; player: DynastyRosterPlayer | null }[];
  benchPlayers: DynastyRosterPlayer[];
  tradeHistory: DynastyTeamTradeHistoryEntry[];
};

export type DynastyTeamTradeHistoryAsset = {
  kind: "player" | "pick" | "faab";
  label: string;
};

export type DynastyTeamTradeHistoryEntry = {
  transactionId: string;
  createdAt: number | null;
  leg: number | null;
  counterpartyRosterIds: number[];
  counterpartyNames: string[];
  sent: DynastyTeamTradeHistoryAsset[];
  received: DynastyTeamTradeHistoryAsset[];
};

export type TradeAsset = DynastyRosterPlayer & {
  sourceTeamId: number;
  sourceTeamName: string;
};

export type FindTradeRecommendation = {
  id: string;
  sourceTeamId: number;
  partnerTeamId: number;
  selectedPlayer: DynastyRosterPlayer;
  myAssets: DynastyRosterPlayer[];
  partnerAssets: DynastyRosterPlayer[];
  adjustedDelta: number;
  balanceAddValue: number;
  explanation: string;
};

export type PlayerLookupResult = {
  team: DynastyRosterTeam;
  player: DynastyRosterPlayer;
};

export type LeagueSettingsSummary = {
  teamCount: number | null;
  starters: number | null;
  bench: number | null;
  taxi: number | null;
  ir: number | null;
  valueModeLabel: "2QB" | "1QB";
  formatTags: string[];
  starterSlots: string[];
};

export type PickValueBucket = "Early" | "Mid" | "Late";

export type PickPlayerAssignment = {
  id: string;
  name: string;
  position: string;
  team?: string;
  value: number | null;
};

export function parseSleeperIdentifier(rawValue: string) {
  const trimmed = rawValue.trim();
  if (!trimmed) return "";
  const fromUrl = trimmed.match(/\/(league)\/(?:nfl\/)?([0-9]+)/i) ?? trimmed.match(/([0-9]{6,})/);
  return fromUrl?.[2] ?? fromUrl?.[1] ?? trimmed;
}

export async function fetchJsonOrThrow<T>(url: string) {
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Request failed with HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export function withCacheBust(url: string) {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}_t=${Date.now()}`;
}

export function getSleeperAvatarUrl(avatarId?: string) {
  const trimmed = String(avatarId ?? "").trim();
  if (!trimmed) return "";
  return `https://sleepercdn.com/avatars/${trimmed}`;
}

export function formatValue(value: number | null) {
  if (value == null || !Number.isFinite(value)) return "\u2014";
  return value.toLocaleString();
}

function getRoundSuffix(round: number) {
  if (round === 1) return "st";
  if (round === 2) return "nd";
  if (round === 3) return "rd";
  return "th";
}

export function buildGenericPickName(season: number, round: number) {
  return `${season} ${round}${getRoundSuffix(round)}`;
}

export function buildBucketedPickName(season: number, round: number, bucket: PickValueBucket) {
  return `${season} ${bucket} ${round}${getRoundSuffix(round)}`;
}

export function buildSpecificPickName(season: number, round: number, slot: number) {
  return `${season} ${round}${getRoundSuffix(round)} Rd - ${round}.${String(slot).padStart(2, "0")}`;
}

export function formatTransactionDate(timestamp: number | null | undefined) {
  if (!timestamp || !Number.isFinite(timestamp)) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(timestamp));
  } catch {
    return "";
  }
}

export function parseSimpleCsvLine(line: string) {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (inQuotes) {
      if (char === "\"") {
        if (line[i + 1] === "\"") {
          current += "\"";
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
      continue;
    }

    if (char === "\"") {
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      cells.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current);
  return cells;
}

export function slugifyPickId(name: string) {
  return `pick-${String(name ?? "")
    .toLowerCase()
    .trim()
    .replace(/'/g, "")
    .replace(/\./g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")}`;
}

export function importPickPlayersFromCsv(csvText: string): Player[] {
  const rows = csvText
    .split(/\r?\n/)
    .map((line) => parseSimpleCsvLine(line))
    .filter((row) => row.some((cell) => String(cell ?? "").trim()));

  if (!rows.length) return [];

  const header = rows[0].map((cell) => String(cell ?? "").trim().toLowerCase());
  const idxName = 0;
  const idxPos = header.findIndex((cell) => cell === "position" || cell === "pos");
  const idxValue = header.findIndex((cell) => cell === "value" || cell === "1qbvalue" || cell === "1qb value");
  const idxSfValue = header.findIndex((cell) => cell === "sfvalue" || cell === "sf value" || cell === "superflex value");

  return rows.slice(1).flatMap((row) => {
    const position = String(row[idxPos] ?? "").trim().toUpperCase();
    if (position !== "PI") return [];

    const name = String(row[idxName] ?? "").trim();
    if (!name) return [];

    const parseNumeric = (raw: unknown) => {
      const cleaned = String(raw ?? "").replace(/,/g, "").trim();
      const value = Number(cleaned);
      return Number.isFinite(value) ? value : undefined;
    };

    return [{
      id: slugifyPickId(name),
      name,
      position: "PI" as any,
      team: "",
      value: parseNumeric(row[idxValue]),
      sfValue: parseNumeric(row[idxSfValue]),
    } satisfies Player];
  });
}

export function sortPlayersByValue(players: DynastyRosterPlayer[]) {
  return [...players].sort((left, right) => {
    const leftIsPick = String(left.position ?? "").toUpperCase() === "PI";
    const rightIsPick = String(right.position ?? "").toUpperCase() === "PI";
    if (leftIsPick !== rightIsPick) {
      return leftIsPick ? 1 : -1;
    }
    const leftValue = left.value ?? Number.NEGATIVE_INFINITY;
    const rightValue = right.value ?? Number.NEGATIVE_INFINITY;
    if (rightValue !== leftValue) return rightValue - leftValue;
    return left.name.localeCompare(right.name);
  });
}

export function normalizeSlot(slot: string) {
  return String(slot ?? "").toUpperCase().replace(/[^A-Z]/g, "");
}

export function getDisplaySlotLabel(slot: string) {
  const normalized = normalizeSlot(slot);
  if (normalized === "SUPERFLEX") return "SF";
  if (normalized === "RECFLX" || normalized === "FLEX" || normalized === "WRRBTE") return "FLEX";
  if (normalized === "WRTE") return "WR/TE";
  if (normalized === "RBWR") return "RB/WR";
  if (normalized === "DST") return "DST";
  return normalized || slot;
}

export function getPositionColor(value: string) {
  const normalized = String(value ?? "").toUpperCase();
  if (normalized === "SF" || normalized === "SUPERFLEX") return posColor("QB");
  if (normalized === "FLEX" || normalized === "RECFLX" || normalized === "WRRBTE") return "#64748b";
  if (normalized === "WR/TE") return "#64748b";
  if (normalized === "RB/WR") return "#64748b";
  if (normalized === "QB" || normalized === "RB" || normalized === "WR" || normalized === "TE" || normalized === "K" || normalized === "DST") {
    return posColor(normalized);
  }
  return "#64748b";
}

export function SlotLabel({ slot, render }: { slot: string; render: (slot: string) => ReactNode }) {
  return <>{render(slot)}</>;
}

export function isTradeablePlayerAsset(player: DynastyRosterPlayer) {
  return ["QB", "RB", "WR", "TE"].includes(String(player.position ?? "").toUpperCase());
}

export function isBenchLikeSlot(slot: string) {
  const normalized = normalizeSlot(slot);
  return normalized === "BN" || normalized === "BENCH" || normalized === "IR" || normalized === "RESERVE" || normalized === "TAXI";
}

export function isEligibleForSlot(player: DynastyRosterPlayer, slot: string) {
  const position = String(player.position ?? "").toUpperCase();
  const normalized = normalizeSlot(slot);

  if (normalized === "QB") return position === "QB";
  if (normalized === "RB") return position === "RB";
  if (normalized === "WR") return position === "WR";
  if (normalized === "TE") return position === "TE";
  if (normalized === "K") return position === "K";
  if (normalized === "DST" || normalized === "DEF") return position === "DST";
  if (normalized === "FLEX" || normalized === "RECFLX" || normalized === "WRRBTE") return ["RB", "WR", "TE"].includes(position);
  if (normalized === "WRTE") return ["WR", "TE"].includes(position);
  if (normalized === "RBWR") return ["RB", "WR"].includes(position);
  if (normalized === "SUPERFLEX" || normalized === "SF" || normalized === "OP") return ["QB", "RB", "WR", "TE"].includes(position);

  return false;
}

export function assignRosterBySlots(players: DynastyRosterPlayer[], starterSlots: string[]) {
  const sortedPlayers = sortPlayersByValue(players);
  const usedIds = new Set<string>();
  const starters: { slot: string; player: DynastyRosterPlayer | null }[] = [];

  for (const slot of starterSlots) {
    const normalized = normalizeSlot(slot);
    let chosen: DynastyRosterPlayer | undefined;

    if (normalized === "SUPERFLEX" || normalized === "SF" || normalized === "OP") {
      chosen = sortedPlayers.find((player) => !usedIds.has(player.id) && String(player.position).toUpperCase() === "QB");
    }

    if (!chosen) {
      chosen = sortedPlayers.find((player) => !usedIds.has(player.id) && isEligibleForSlot(player, slot));
    }

    if (chosen) usedIds.add(chosen.id);
    starters.push({ slot: getDisplaySlotLabel(slot), player: chosen ?? null });
  }

  const benchPlayers = sortedPlayers.filter((player) => !usedIds.has(player.id));
  return { starters, benchPlayers };
}

export function countRosterSlots(positions: string[] | undefined, matcher: (value: string) => boolean) {
  if (!Array.isArray(positions)) return 0;
  return positions.filter((slot) => matcher(String(slot ?? "").toUpperCase())).length;
}

export function buildLeagueSettingsSummary(league: SleeperLeague): LeagueSettingsSummary {
  const rosterPositions = Array.isArray(league.roster_positions) ? league.roster_positions : [];
  const settings = league.settings ?? {};
  const scoring = league.scoring_settings ?? {};

  const starters = countRosterSlots(
    rosterPositions,
    (slot) => !["BN", "BENCH", "IR", "RESERVE", "TAXI"].includes(slot)
  );
  const bench = countRosterSlots(rosterPositions, (slot) => slot === "BN" || slot === "BENCH");
  const taxiFromSettings = Number(settings.taxi_slots ?? 0);
  const taxiFromRoster = countRosterSlots(rosterPositions, (slot) => slot === "TAXI");
  const irSlots = Number(settings.reserve_slots ?? 0) || countRosterSlots(rosterPositions, (slot) => slot === "IR" || slot === "RESERVE");
  const hasSuperflexSlot = rosterPositions.some((slot) => {
    const normalized = String(slot ?? "").toUpperCase().replace(/[^A-Z]/g, "");
    return normalized === "SUPERFLEX" || normalized === "SF";
  });
  const tightEndReception = Number(scoring.rec_te ?? 0);
  const regularReception = Number(scoring.rec ?? 0);
  const formatTags: string[] = [];
  const starterSlots = rosterPositions.filter((slot) => !isBenchLikeSlot(slot));

  formatTags.push(hasSuperflexSlot ? "Superflex" : "1QB");
  if (tightEndReception > regularReception) formatTags.push("TE Premium");
  if ((taxiFromSettings || taxiFromRoster) > 0) formatTags.push("Taxi");
  if (irSlots > 0) formatTags.push("IR");

  return {
    teamCount: Number(league.total_rosters ?? 0) || null,
    starters: starters || null,
    bench: bench || null,
    taxi: taxiFromSettings || taxiFromRoster || null,
    ir: irSlots || null,
    valueModeLabel: hasSuperflexSlot ? "2QB" : "1QB",
    formatTags,
    starterSlots,
  };
}
